import { apiUrl, apiUrlV2 } from "../constants/apiUrl"
import createSearchParams from "../utilities/createSearchParams"
import rollbarLogger from "../utilities/rollbarLogger"
import { adaptDealer } from "../utilities/auditDealerAdapters"

const initialState = {
  dealers: [],
  dealer: undefined,
  dealersFetching: false,
  dealerFilterAttribute: 'default',
  dealerFilterDirection: 'filter[direction]=DESC',
  dealerTableFilterParams: '',
  nearbyDealerTableFilterParams: '',
  nearbyDealerFilterAttribute: 'default',
  dealerPaginationData: {},
  selectedDealerScopeLabel: null,
  dealersSearchBarValue: '',
  nearbyDealersSearchBarValue: '',
  isUtilizationSelected: false,
  dealersPage: 1,
  nearbyDealers: [],
  nearbyDealersFetching: false,
  nearbyDealerPaginationData: {},
  auditDealers: [],
  auditDealersFetching: false,
  auditDealersPage: 1,
}

const createDealersSlice = (set, get) => ({
  ...initialState,
  setDealerFilterAttribute: (attribute) => {
    set((state) => ({ dealerFilterAttribute: attribute }))
  },
  setNearbyDealerFilterAttribute: (attribute) => {
    set((state) => ({ nearbyDealerFilterAttribute: attribute }))
  },
  setDealerFilterDirection: (direction) => {
    set((state) => ({ dealerFilterDirection: direction }))
  },
  setDealerTableFilterParams: (params) => {
    set((state) => ({ dealerTableFilterParams: params }))
  },
  setNearbyDealerTableFilterParams: (params) => {
    set((state) => ({ nearbyDealerTableFilterParams: params }))
  },
  setSelectedDealerScopeLabel: (params) => {
    set((state) => ({ selectedDealerScopeLabel: params }))
  },
  setDealersSearchBarValue: (params) => {
    set((state) => ({ dealersSearchBarValue: params }))
  },
  setNearbyDealersSearchBarValue: (params) => {
    set((state) => ({ nearbyDealersSearchBarValue: params }))
  },
  setIsUtilizationSelected: (boolean) => {
    set((state) => ({ isUtilizationSelected: boolean }))
  },
  setDealersPage: (page) => {
    set((state) => ({ dealersPage: page }))
  },
  setAuditDealersPage: (page) => {
    set((state) => ({ auditDealersPage: page }))
  },
  fetchDealers: async (params) => {
    set((state) => ({ dealersFetching: true }))
    try {
      const response = await fetch(
        `${apiUrlV2}/dealers?branch_id=${get().selectedBranch[0]}&page=${get().dealersPage}&${get().dealerFilterAttribute}&${get().dealerFilterDirection}&${createSearchParams(params)}`,
        { headers: get().requestHeaders }
      )
      if (response.status === 200) {
        const data = await response.json()

        if (get().user) {
          set((state) => ({
            dealers: data.data,
            dealersFetching: false,
            dealerPaginationData: {
              dealerResultsCount: response.headers.get('x-total'),
              lastPage: response.headers.get('x-last-page'),
              totalPages: response.headers.get('x-total-pages'),
              currentPage: Number(response.headers.get('x-page')) - 1
            }
          }))
        }
      }
    } catch (err) {
      get().logError('There was an error fetching dealers.', err)
    }
  },
  fetchAuditDealers: async () => {
    set((state) => ({ auditDealersFetching: true }))
    try {
      const auditDealerParams = {
        // Max dealers per branch is 60, so a single large page returns them all
        per_page: 500,
        'sort[field_audit_due_on]': 'true',
        'sort[field_audit_due_on_direction]': 'ASC',
        'filter[has_auditable_units]': 'true',
        // 1 = Active, 2 = Inactive, 4 = Suspended, 5 = Collection
        status: [1, 2, 4, 5]
      }
      const response = await fetch(
        `${apiUrlV2}/dealers?branch_id=${get().selectedBranch[0]}&${createSearchParams(auditDealerParams)}`,
        { headers: get().requestHeaders }
      )
      if (response.status === 200) {
        const data = await response.json()

        if (get().user) {
          set((state) => ({
            auditDealers: (data.data ?? []).map(adaptDealer),
            auditDealersFetching: false,
            auditDealersPage: 1,
          }))
        }
      }
    } catch (err) {
      get().logError('There was an error fetching audit dealers.', err)
    }
  },
  fetchNearbyDealers: async (lat, lng, distance, params) => {
    set((state) => ({ nearbyDealersFetching: true }))
    try {
      const searchParams = new URLSearchParams({
        branch_id: get().selectedBranch[0],
        page: get().dealersPage,
        'filter[lat]': lat,
        'filter[lng]': lng,
        'filter[distance]': distance,
      })

      const response = await fetch(
        `${apiUrlV2}/dealers?${searchParams}&${get().nearbyDealerFilterAttribute}&${createSearchParams(params)}`,
        { headers: get().requestHeaders }
      )
      if (response.status === 200) {
        const data = await response.json()

        if (get().user) {
          set((state) => ({
            nearbyDealers: data.data,
            nearbyDealersFetching: false,
            nearbyDealerPaginationData: {
              dealerResultsCount: response.headers.get('x-total'),
              lastPage: response.headers.get('x-last-page'),
              totalPages: response.headers.get('x-total-pages'),
              currentPage: Number(response.headers.get('x-page')) - 1
            }
          }))
        }
      }
    } catch (err) {
      get().logError('There was an error fetching nearby dealers.', err)
    }
  },

  resetDealersSlice: () => set(initialState)
})

export default createDealersSlice
